import React from 'react';
import { ChevronRight } from 'lucide-react';
import { displayBotanicalSubtitle, displayPrimaryPlantName } from '../utils/plantDisplayName';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function emojiFor(plantType) {
  switch ((plantType || '').toLowerCase()) {
    case 'tree':      return '🌳';
    case 'shrub':     return '🪴';
    case 'herb':      return '🌿';
    case 'vegetable': return '🥬';
    case 'lawn':      return '🌱';
    case 'annual':    return '🌸';
    case 'perennial': return '🌼';
    default:          return '🌿';
  }
}

function bloomLabel(plant) {
  const s = plant.bloom_month_start;
  const e = plant.bloom_month_end;
  if (!s && !e) return null;
  if (s && e && s !== e) return `${MONTHS[s - 1]}–${MONTHS[e - 1]}`;
  return MONTHS[(s || e) - 1];
}

// Compact card for the plant library grid/list.
// Shows emoji, names, location, bloom season, and a tiny 4-dot photo strip
// indicator (filled dots = seasons with photos).
export default function PlantCard({ plant, onClick }) {
  const name = displayPrimaryPlantName(plant);
  const botanical = displayBotanicalSubtitle(plant);
  const bloom = bloomLabel(plant);
  const photoDots = [
    { k: 'spring', filled: Number(plant.photo_spring) > 0 },
    { k: 'summer', filled: Number(plant.photo_summer) > 0 },
    { k: 'fall',   filled: Number(plant.photo_fall)   > 0 },
    { k: 'winter', filled: Number(plant.photo_winter) > 0 },
  ];

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${name}`}
      className="w-full text-left rounded-2xl bg-white border border-black/5 shadow-sm p-3 flex gap-3 items-center cursor-pointer transition hover:bg-garden-primary/5 hover:border-garden-primary/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-garden-primary active:scale-[0.99]"
    >
      <div className="w-12 h-12 rounded-full bg-garden-primaryLight flex items-center justify-center text-2xl shrink-0 overflow-hidden">
        {plant.thumbnail_url ? (
          <img
            src={plant.thumbnail_url}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <span aria-hidden>{emojiFor(plant.plant_type)}</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-garden-ink truncate">{name}</div>
        {botanical ? (
          <div className="text-xs italic text-garden-muted truncate">
            {botanical}
          </div>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-garden-muted">
          {plant.location_notes ? (
            <span className="truncate max-w-[180px]">📍 {plant.location_notes}</span>
          ) : null}
          {bloom ? <span>🌸 {bloom}</span> : null}
          {plant.plant_type ? <span className="capitalize">{plant.plant_type}</span> : null}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-0.5" aria-label="Seasonal photos available">
            {photoDots.map((d) => (
              <span
                key={d.k}
                className={
                  'inline-block w-1.5 h-1.5 rounded-full ' +
                  (d.filled ? 'bg-garden-primary' : 'bg-black/15')
                }
                title={d.k}
              />
            ))}
          </div>
        </div>
        <ChevronRight
          size={18}
          className="text-garden-muted shrink-0"
          aria-hidden
        />
      </div>
    </button>
  );
}
